<template>
    <div id="contentcontainter" class="standard-mode" role="main" aria-labelledby="settings-page-title">
        <!-- SR-H1 für Seitenkontext -->
        <h1 id="settings-page-title" class="sr-only">Settings</h1>

        <!-- Info-Snackbar als Live-Region -->
        <v-snackbar top :color="info.color" v-model="showInfo" role="status" aria-live="polite" aria-atomic="true" tabindex="-1">
            <v-icon aria-hidden="true">{{ info.icon }}</v-icon>
            <span class="font-weight-bold pl-2">{{ info.message }}</span>
        </v-snackbar>

        <v-card class="mt-10 mx-auto quaroshadow rounded_card" max-width="1250px" role="region" aria-labelledby="settings-toolbar-title">
            <!-- toolbar start -->
            <v-toolbar color="primary" flat>
                <v-toolbar-title color="white" style="color: white !important">
                    <v-row>
                        <v-col style="display: flex">
                            <span id="settings-toolbar-title" class="tabheading">Settings</span>
                        </v-col>
                    </v-row>
                </v-toolbar-title>

                <template v-slot:extension>
                    <!-- ARIA-Label für den Tab-Bereich, sonst Standard lassen -->
                    <v-tabs id="settings-tabs" v-model="tab" align-with-title color="white" dark class="white--text" aria-label="Settings sections">
                        <v-tabs-slider></v-tabs-slider>
                        <v-tab v-for="item in visibleItems" :key="item.id" class="ml-0">
                            {{ item.text }}
                            <span v-if="item.badge" class="tab-badge ml-2">{{ item.badge }}</span>
                        </v-tab>
                    </v-tabs>
                </template>
            </v-toolbar>

            <!-- Tab-Panels -->
            <v-tabs-items v-model="tab">
                <v-tab-item v-for="item in visibleItems" :key="item.id">
                    <v-card flat>
                        <v-card-text class="pa-0">
                            <UserTable :domains="domains" v-if="item.id == 1 && $store.state.admin"></UserTable>

                            <CategorySettings v-else-if="item.id == 2"></CategorySettings>

                            <template v-else-if="item.id == 3 && $store.state.admin">
                                <v-row v-for="(domain, i) in domains" :key="i" class="mt-5 px-5">
                                    <v-col md="11">
                                        <!-- SR-Hilfe bleibt, nur per aria-describedby anbinden -->
                                        <p :id="`domain-hint-${i}`" class="sr-only">New users registering with this domain will be automatically assigned to your database.</p>

                                        <v-text-field
                                            label="Company domain"
                                            hint="New users registering with this domain will be automatically assigned to your database!"
                                            class="pr-5 pl-2"
                                            rounded
                                            filled
                                            persistent-hint
                                            dense
                                            type="url"
                                            v-model="domains[i]"
                                            :aria-describedby="`domain-hint-${i}`" />
                                    </v-col>

                                    <v-col md="1" style="display: flex">
                                        <v-btn color="primary" large class="mt-1" rounded type="button" @click="saveDomain(i)" :aria-label="`Save company domain ${domains[i] || ''}`"> Save </v-btn>
                                    </v-col>
                                </v-row>

                                <v-row v-if="$store.state.user_api_key_feature_enabled" class="mt-5 px-5">
                                    <v-col cols="12">
                                        <v-card outlined class="pa-4 rounded-lg">
                                            <div class="font-weight-bold text-h6 mb-1">Access for users</div>
                                            <p class="mb-4 user-access-help-text">Controls whether all non-admin users in this company can see these pages. Admins always see them.</p>

                                            <v-switch
                                                v-model="allowUserApiKey"
                                                label="Users may manage their own API key"
                                                hide-details
                                                class="mt-0"
                                                @change="changeUserAccess" />
                                            <v-switch
                                                v-model="allowUserMcpPage"
                                                label="Users may see the MCP server page"
                                                hide-details
                                                class="mt-3"
                                                @change="changeUserAccess" />
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </template>

                            <Credits v-else-if="item.id == 4 && $store.state.admin"></Credits>

                            <ApiKeySettings v-else-if="item.id == 5" @info="createInfo" @navigate="goToTab"></ApiKeySettings>

                            <McpSettings v-else-if="item.id == 6" @info="createInfo" @navigate="goToTab"></McpSettings>

                            <SecuritySettings v-else-if="item.id == 7" @info="createInfo"></SecuritySettings>
                        </v-card-text>
                    </v-card>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
    </div>
</template>

<script>
    import CategorySettings from "../components/CategorySettings"
    import UserTable from "../components/tables/UserTable"
    import restricted_domains from "../components/helper/restricted_domains"
    import Credits from "../components/Credits.vue"
    import ApiKeySettings from "../components/ApiKeySettings.vue"
    import McpSettings from "../components/McpSettings.vue"
    import SecuritySettings from "../components/SecuritySettings.vue"

    export default {
        components: {
            CategorySettings,
            UserTable,
            Credits,
            ApiKeySettings,
            McpSettings,
            SecuritySettings
        },
        data() {
            return {
                info: {
                    message: null,
                    icon: null,
                    color: null
                },
                showInfo: false,
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                tab: null,
                items: [
                    //tab items , dont change id -> used for template
                    { text: "User Management", id: 1 },
                    // { text: "Categories", id: 2 },
                    { text: "Account", id: 3 },
                    { text: "Credit Overview", id: 4 },
                    { text: "API", id: 5 },
                    { text: "MCP Server", id: 6, badge: "New" },
                    { text: "Security", id: 7 }
                ],
                domains: [],
                allowUserApiKey: true,
                allowUserMcpPage: true
            }
        },
        computed: {
            visibleItems() {
                // user_api_key_feature_enabled=false (Stand 2026-09-25): Personal-Keys sind abgeschaltet. Admins
                // sehen API-Tab (id 5) und MCP-Tab (id 6) trotzdem - beide zeigen dann nur den Company-Key
                // (ApiKeySettings.vue / McpSettings.vue, siehe usePersonalKey).
                // Reaktivieren: user_api_key_feature_enabled im Store auf true setzen - hier aendert sich dann
                // nichts mehr, die Freigaben-Logik unten greift automatisch wieder.
                const featureEnabled = this.$store.state.user_api_key_feature_enabled
                const items = this.items
                if (this.$store.state.admin) return items
                if (!featureEnabled) return items.filter((item) => item.id == 7)
                // Security-Tab (id 7) sehen alle User, unabhaengig von den Freigaben fuer API-Key/MCP -
                // 2FA ist ein persoenliches Konto-Setting, kein von Admins zu gewaehrendes Feature.
                return items.filter((item) => item.id == 7 || (item.id == 5 && this.$store.state.allow_user_api_key) || (item.id == 6 && this.$store.state.allow_user_mcp_page))
            }
        },
        methods: {
            createInfo(message, color, icon) {
                this.info.message = message
                this.info.color = color
                this.info.icon = icon
                this.showInfo = true
            },
            goToTab(id) {
                const index = this.visibleItems.findIndex((item) => item.id == id)
                if (index > -1) this.tab = index
            },
            openTabFromRoute() {
                const query = this.$route.query.tab
                if (!query) return
                const index = this.items.findIndex((item) => item.slug == query || item.id == query)
                if (index > -1) this.tab = index
            },
            saveDomain(i) {
                console.log("SAVVEEE")
                console.log(this.domains[i])
                console.log(this.domainsSafe[i])

                if (this.domains[i] == this.domainsSafe[i]) return
                console.log(restricted_domains)
                if (this.domains[i].indexOf("www") > -1 || this.domains[i].indexOf("http") > -1) {
                    this.domains = JSON.parse(JSON.stringify(this.domainsSafe))
                    this.createInfo("Please enter a domain without www, http or https.", "#fcba03", "mdi-alert-circle-outline")
                    return
                }
                if (restricted_domains.domains.indexOf(this.domains[i]) == -1) {
                    this.$http
                        .post(
                            process.env.VUE_APP_APIURL + "/company/domains/change",
                            { company_id: localStorage.company_id, index: i, new_domain: this.domains[i] },
                            { headers: { Authorization: this.auth } }
                        )
                        .then((response) => {
                            console.log(response.body)
                            if (response.body.domain_registered) {
                                this.createInfo("Domain changed!", "green", "mdi-check-circle-outline")
                                this.domainsSafe = JSON.parse(JSON.stringify(this.domains))
                            } else {
                                this.createInfo("This domain is already in use! For further assistance, please contact the support.", "#fcba03", "mdi-alert-circle-outline")
                                this.domains = JSON.parse(JSON.stringify(this.domainsSafe))
                            }
                        })
                        .catch((err) => {
                            console.log(err)
                        })
                } else {
                    this.domains = JSON.parse(JSON.stringify(this.domainsSafe))
                    this.createInfo("This domain is not allowed! For further assistance, please contact the support.", "#fcba03", "mdi-alert-circle-outline")
                }
            },
            fetchCompanyDomains() {
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/company/domains", { company_id: localStorage.company_id }, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        console.log(response.body)
                        this.domains = JSON.parse(response.body.verification_domain)
                        this.domainsSafe = JSON.parse(response.body.verification_domain)
                        console.log(this.domains)
                    })
                    .catch((err) => {
                        console.log(err)
                    })
            },
            // Admin-Schalter, ob Nicht-Admin-User dieser Company die eigene API-Key-Verwaltung bzw. die
            // MCP-Seite sehen. Gilt für alle Nicht-Admin-User auf einmal (vorerst kein Per-User-Feintuning).
            changeUserAccess() {
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/company/settings/user-access/change",
                        {
                            user_id: localStorage.id,
                            company_id: localStorage.company_id,
                            allow_user_api_key: this.allowUserApiKey,
                            allow_user_mcp_page: this.allowUserMcpPage
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then(() => {
                        this.$store.commit("SET_ALLOW_USER_API_KEY", this.allowUserApiKey)
                        this.$store.commit("SET_ALLOW_USER_MCP_PAGE", this.allowUserMcpPage)
                        this.createInfo("Freigaben gespeichert.", "green", "mdi-check-circle-outline")
                    })
                    .catch((error) => {
                        console.log(error)
                        this.allowUserApiKey = this.$store.state.allow_user_api_key
                        this.allowUserMcpPage = this.$store.state.allow_user_mcp_page
                        if (error && error.status === 403) {
                            this.createInfo("Nur Admins können diese Freigaben ändern.", "#fcba03", "mdi-alert-circle-outline")
                        } else {
                            this.createInfo("Freigaben konnten nicht gespeichert werden.", "#fcba03", "mdi-alert-circle-outline")
                        }
                    })
            }
        },
        watch: {
            tab(index) {
                const item = this.items[index]
                if (!item || this.$route.query.tab == item.slug) return
                this.$router.replace({ query: { ...this.$route.query, tab: item.slug } }).catch(() => {})
            },
            "$route.query.tab"() {
                this.openTabFromRoute()
            }
        },
        mounted() {
            this.openTabFromRoute()
            this.fetchCompanyDomains()
            this.allowUserApiKey = this.$store.state.allow_user_api_key
            this.allowUserMcpPage = this.$store.state.allow_user_mcp_page

            //test end check
            if (this.$store.state.company_status === "test_ended") {
                this.$router.push("/endoftest")
            }
        }
    }
</script>
<style>
    .tab-badge {
        background: #ffffff;
        color: #008470;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.4px;
        padding: 3px 7px;
        border-radius: 8px;
    }

    .tabheading {
        padding-top: 1px;
        padding-left: 4px;
        font-size: 24px;
    }
    #settings-tabs .v-tab:not(.v-tab--active) {
        color: rgb(255 255 255) !important;
    }
    #settings-tabs .v-tab--active {
        border-top: 2px solid white !important;
    }
    .user-access-help-text {
        color: #4c4c4c;
    }
</style>
